/**
 * Theme front script (ESM) — cookie, forms, motion dynamic import.
 */

const cfg = window.sskladTheme || {};
const cookieName = cfg.cookieName || 'ssklad_cookie_consent';
const cookiePath = cfg.cookiePath || '/';
const cookieMaxAge = cfg.cookieMaxAge || 31536000;

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : '';
}

function setCookie(name, value, maxAge, path) {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    name + '=' + encodeURIComponent(value) + '; Max-Age=' + maxAge + '; Path=' + path + '; SameSite=Lax' + secure;
}

function initDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function pushEvent(name, params) {
  initDataLayer();
  const payload = Object.assign({ event: name }, params || {});
  window.dataLayer.push(payload);
  if (cfg.debugAnalytics && typeof console !== 'undefined' && console.debug) {
    console.debug('[ssklad analytics]', payload);
  }
}

function loadAnalyticsAfterConsent() {
  pushEvent('ssklad_analytics_ready', { consent: true });
}

function initCookieBanner() {
  const banner = document.getElementById('ssklad-cookie-banner');
  const accept = document.getElementById('ssklad-cookie-accept');
  if (!banner || !accept) {
    return;
  }
  if (getCookie(cookieName) === '1') {
    loadAnalyticsAfterConsent();
    return;
  }
  banner.classList.add('is-visible');
  accept.addEventListener('click', () => {
    setCookie(cookieName, '1', cookieMaxAge, cookiePath);
    try {
      localStorage.setItem('ssklad_cookie_consent', 'accepted');
    } catch (e) {
      /* ignore */
    }
    banner.classList.remove('is-visible');
    pushEvent('ssklad_cookie_accept', {});
    loadAnalyticsAfterConsent();
  });
}

function initCtaTracking() {
  document.addEventListener('click', (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) {
      return;
    }
    const el = target.closest('[data-cta-id]');
    if (!el) {
      return;
    }
    pushEvent('ssklad_cta_click', {
      cta_id: el.getAttribute('data-cta-id') || '',
      page_path: location.pathname,
    });
  });
}

function initMobileNav() {
  const toggle = document.querySelector('[data-ssklad-nav-toggle]');
  const nav = document.getElementById('ssklad-primary-nav');
  if (!toggle || !nav) {
    return;
  }
  toggle.addEventListener('click', () => {
    const open = nav.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
}

function initHeaderScroll() {
  const header = document.querySelector('[data-ssklad-header]');
  if (!header) {
    return;
  }
  const hero = document.getElementById('hero') || document.querySelector('.ssklad-section--page-hero');
  const onScroll = () => {
    let scrolled = window.scrollY > 8;
    if (hero) {
      const rect = hero.getBoundingClientRect();
      scrolled = rect.bottom < 72;
    }
    header.classList.toggle('is-scrolled', scrolled);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
}

function initFaqAccordion() {
  document.querySelectorAll('.faq-item__trigger').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const panelId = btn.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (panel) {
        if (expanded) {
          panel.setAttribute('hidden', '');
        } else {
          panel.removeAttribute('hidden');
        }
      }
      const icon = btn.querySelector('.faq-item__icon');
      if (icon) {
        icon.textContent = expanded ? '+' : '−';
      }
      if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
        window.ScrollTrigger.refresh();
      }
    });
  });
}

function initLeadForm() {
  const form = document.getElementById('ssklad-lead-form');
  if (!form) {
    return;
  }
  const status = document.getElementById('ssklad-lead-status');
  const params = new URLSearchParams(location.search);
  const productSelect = form.querySelector('[name="product"]');
  if (productSelect && params.get('product')) {
    productSelect.value = params.get('product');
  }
  document.querySelectorAll('[data-product-prefill]').forEach((link) => {
    link.addEventListener('click', () => {
      const val = link.getAttribute('data-product-prefill');
      if (productSelect && val) {
        productSelect.value = val;
      }
    });
  });
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const fd = new FormData(form);
    const payload = {
      name: fd.get('name'),
      phone: fd.get('phone'),
      email: fd.get('email'),
      product: fd.get('product'),
      message: fd.get('message'),
      company_website: fd.get('company_website'),
      pd_consent: fd.get('pd_consent') ? 1 : 0,
    };
    fetch((cfg.restUrl || '') + 'lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': cfg.nonce || '' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then((result) => {
        if (status) {
          status.textContent =
            (result.data && result.data.message) || (result.ok ? 'Заявка отправлена.' : 'Ошибка отправки.');
          status.classList.toggle('is-error', !result.ok || !(result.data && result.data.ok));
        }
        if (result.ok && result.data && result.data.ok) {
          form.reset();
          pushEvent('ssklad_lead_submit', { status: 'success' });
        }
      })
      .catch(() => {
        if (status) {
          status.textContent =
            'Не удалось отправить форму. Проверьте поля или позвоните +7 (495) 880-12-40.';
          status.classList.add('is-error');
        }
      });
  });
}

function initReducedMotion() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    document.documentElement.setAttribute('data-reduced-motion', 'true');
    document.documentElement.classList.add('motion-reduced');
  }
  return reduced;
}

function isHomePage() {
  return (
    document.body.classList.contains('home') ||
    !!document.querySelector('.ssklad-main--front')
  );
}

function isInnerMotionPage() {
  const main = document.querySelector('.ssklad-main');
  if (!main) {
    return false;
  }
  return (
    main.classList.contains('ssklad-main--pricing') ||
    main.classList.contains('ssklad-main--integrations') ||
    main.classList.contains('ssklad-main--about') ||
    main.classList.contains('ssklad-main--blog')
  );
}

async function loadMotionModule(url, exportNames) {
  const mod = await import(/* webpackIgnore: true */ url);
  for (const name of exportNames) {
    const fn = mod.default || mod[name];
    if (typeof fn === 'function') {
      fn();
      return;
    }
  }
}

async function initMotionHooks() {
  const reduced = initReducedMotion();

  const run = async () => {
    try {
      if (reduced && cfg.motionLiteUrl) {
        await loadMotionModule(cfg.motionLiteUrl, ['initMotionLite']);
        return;
      }
      if (isHomePage() && cfg.motionHomeUrl) {
        await loadMotionModule(cfg.motionHomeUrl, ['initMotionHome']);
        return;
      }
      if (isInnerMotionPage() && cfg.motionInnerUrl) {
        await loadMotionModule(cfg.motionInnerUrl, ['initMotionInner']);
        return;
      }
    } catch (err) {
      if (cfg.debugAnalytics && console.warn) {
        console.warn('[ssklad motion load]', err);
      }
      if (cfg.motionLiteUrl) {
        try {
          await loadMotionModule(cfg.motionLiteUrl, ['initMotionLite']);
        } catch (liteErr) {
          document.documentElement.classList.add('js-motion-ready');
        }
        return;
      }
    }
    document.documentElement.classList.add('js-motion-ready');
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => run(), { timeout: 2000 });
  } else {
    setTimeout(run, 80);
  }
}

function boot() {
  initCookieBanner();
  initCtaTracking();
  initMobileNav();
  initHeaderScroll();
  initFaqAccordion();
  initLeadForm();
  initMotionHooks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
